//Libraries
import React, {FC, useState, useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Paper, Stack, Alert, Divider, Grid} from '@mui/material';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import PlaceIcon from '@mui/icons-material/Place';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

//Providers
import {useOrgService} from 'src/services';
import {useReferences} from 'src/services';
import {useAuth} from 'src/contexts';

//Interfaces & Typing
import {IAIRecord} from 'src/types/org/ai/aiInterface';
import {IOpsRequest} from 'src/types/org/operation/requestInterface';
import {ISummary} from 'src/types';
import {IOption} from 'src/generic/component/form';

//Processing
import {loadSummaries} from 'src/services/schoolRequest';
import {formatDate} from 'src/utils';
import {getFile} from 'src/services/schoolRequest';
import {formatAddress} from 'src/generic/component/address';
import {ChangeManager} from 'src/generic/utils/form';

//Components
import {Title, Loader, Highlight, FieldNote, AntSwitch, DataValue} from 'src/generic/component';
import {SubTitle} from 'src/generic/component';
import {Button, PrimaryButton} from "@cb/apricot-react";
import {APP_URLS} from "../../constants";

/**
 * TO-DO: Move these to standard location
 * export getLabel function for retrieving a label by key
 */
const ingestionOptions = [{key: 1, label: 'Normal'}, {key: 2, label: 'State Contract'}];
const aiStatusOptions = [{key: 2, label: 'Active'}, {key: 1, label: 'Inactive'}];
const aiCategoryOptions = [{key: 1, label: 'Attending'}, {key: 2, label: 'Ambassador'}];
const aiLevelOptions = [{key: 1, label: 'Level 1: Receive Score Reports'}, {
    key: 2,
    label: 'Level 2: Administer Tests & Receive Score Reports'
}];

const DataField: FC<{ name: string | React.ReactNode, value: string | React.ReactNode; }> = ({name, value}) => {
    return <div style={{display: 'flex', marginBottom: '2px'}}>
        <div style={{width: '250px'}}>
            {name}
        </div>
        <DataValue style={{
            // textAlign: 'right', flexGrow: 1
        }}>
            {value}
    </DataValue>
  </div>;
};

const useAi = (orgId: string, aiCode: string) => {
  const [data, setData] = useState<IAIRecord | undefined>();
  const [isLoading, setLoading] = useState<boolean>(false);
  const service = useOrgService();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const ai = await service.findAi(aiCode, orgId);
        setData(ai);
      } catch (error) {

      }
      setLoading(false);
    })();
  }, [aiCode, orgId]);

  return { data, isLoading };
};

const useHistory = (orgId: string, aiCode: string) => {
  const [data, setData] = useState<ISummary[]>([]);
  const [isLoading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const summaries = await loadSummaries({});
        const history = summaries.all().filter((summary) => {
          return summary.processStatus === 'completed' && summary.orgId === orgId && summary.aiCode === aiCode;
        });
        setData(history);
      } catch (error) {

      }
      setLoading(false);
    })();
  }, [aiCode, orgId]);

  return { data, isLoading };
};

interface IHistoryDetailsProps {
  summary: ISummary;
}

const useSaveRecord = (summary: ISummary) => {
  const [data, setData] = useState<IOpsRequest<IAIRecord> | undefined>();
  const [isLoading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (summary.operationSubmission) {
          const request = await getFile(summary.operationSubmission);
          setData(request);
        }
      } catch (error) {

      }
      setLoading(false);
    })();
  }, [summary]);

  return { data, isLoading };

};

const getLabel = (key: any, map: IOption[]) => {
    return map.find((item) => item.key === key)?.label;
};

const orgGeneralFields = ['org.name', 'org.nameShort', 'org.nameAbbr', 'org.email', 'org.phone', 'org.fax', 'org.visitingAddress', 'org.shippingAddress'];
const orgTypeFields = ['org.orgTypeCd', 'org.orgSubTypeCd', 'org.campus', 'org.charterInd', 'org.educationDeliveryMethodCds'];
const orgProprietaryFields = ['org.mdrPid', 'org.ipedsId', 'org.ncesId', 'org.feeWaiverId'];
const aiGeneralFields = ['ai.aiCode', 'ai.aiCategoryCd', 'ai.aiLevelCd', 'ai.initialIngestionPathCd', 'ai.aiStatusCd'];
const aiFlagFields = ['ai.apEligibilityInd', 'ai.psatEligibilityInd', 'ai.satEligibilityInd', 'ai.clepEligibilityInd', 'ai.ssdEligibilityInd', 'ai.annualReviewInd', 'ai.stateHomeSchoolInd', 'ai.crossPrgSecHoldInd'];

const DELIVERY_METHOD_OPTIONS: any = {
    5: 'Classroom',
    4: 'Internet'
}

const HistoryDetails: FC<IHistoryDetailsProps> = ({summary}) => {
    const {roles} = useAuth();
    const isAdmin = roles.includes('XPM_ADMIN');
    const showProtected = isAdmin;

    const {data, isLoading: recLoading} = useSaveRecord(summary);
    const {types, subTypes, isLoading: refLoading} = useReferences();

    const isLoading = recLoading || refLoading;

  let details = <Loader></Loader>;
  if (!isLoading && data) {
    const { isChanged, getCurrent, getChange, getChangeColor } = ChangeManager<IAIRecord>(data.original || {}, data.request);

    const hasSectionChanges = (fields: string[]) => {
      for (const field of fields) {
        if (isChanged(field)) {
          return true;
        }
      }
      return false;
    };

    const BuildChange = (field: string, map?: IOption[]) => {
      const current = (map ? getLabel(getCurrent(field), map) : getCurrent(field));
      const change = (map ? getLabel(getChange(field), map) : getChange(field));
      console.log(field, change);
      const color = getChangeColor(field);
      const format = (value: any) => {
        if (typeof value === 'boolean') {
          return value === true ? 'Yes' : 'No';
        } else {
          return value || '(empty)';
        }
      };

      return <>
        <div style={{ display: 'inline-block' }} >{format(current)}</div>
        &nbsp;<ArrowRightAltIcon></ArrowRightAltIcon>&nbsp;
        <div style={{ display: 'inline-block' }} className={color || ''}>{format(change)}</div>
      </>;
    };

    const { org } = data?.request || {};
    const { visitingAddress, shippingAddress } = org || {};

    details = <div>
      <h6 style={{ marginBottom: '15px' }}>Organization Profile</h6>
      <Paper elevation={2} style={{ padding: '20px', marginBottom: '15px' }}>
        {hasSectionChanges(orgGeneralFields.concat(orgTypeFields, orgProprietaryFields)) ?
          <>
            {hasSectionChanges(orgGeneralFields) &&
              <>
                <div style={{ marginBottom: '15px' }}>
                  {isChanged('org.name') &&
                    <DataField name='Name' value={BuildChange('org.name')}></DataField>
                  }

                  {isChanged('org.nameShort') &&
                    <DataField name='Short Name' value={BuildChange('org.nameShort')}></DataField>
                  }

                  {isChanged('org.nameAbbr') &&
                    <DataField name='Abbreviated Name' value={BuildChange('org.nameAbbr')}></DataField>
                  }

                  {isChanged('org.website') &&
                    <DataField name='Website' value={BuildChange('org.website')}></DataField>
                  }

                  {isChanged('org.phone.formatted') &&
                    <DataField name='Phone' value={BuildChange('org.phone.formatted')}></DataField>
                  }

                  {isChanged('org.fax.formatted') &&
                      <DataField name='Fax' value={BuildChange('org.fax.formatted')}></DataField>
                  }

                  {isChanged('org.email') &&
                    <DataField name='Email' value={BuildChange('org.email')}></DataField>
                  }
                </div>

                <div>
                  {isChanged('org.visitingAddress') &&
                    <DataField name='Visiting Address' value={<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                      <div>
                        {getCurrent('org.visitingAddress') ? formatAddress(getCurrent('org.visitingAddress')) : '(empty)'}
                      </div>
                      <div>
                        &nbsp;<ArrowRightAltIcon></ArrowRightAltIcon>&nbsp;
                      </div>
                      <div className={getChangeColor('org.visitingAddress')}>
                        {visitingAddress ? formatAddress(visitingAddress) : '(empty)'}
                      </div>
                    </div>}></DataField>
                  }
                </div>

                <div>
                  {isChanged('org.shippingAddress') &&
                    <DataField name='Shipping Address' value={<div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                      <div>
                        {getCurrent('org.shippingAddress') ? formatAddress(getCurrent('org.shippingAddress')) : '(empty)'}
                      </div>
                      <div>
                        &nbsp;<ArrowRightAltIcon></ArrowRightAltIcon>&nbsp;
                      </div>
                      <div className={getChangeColor('org.shippingAddress')}>
                        {shippingAddress ? formatAddress(shippingAddress) : '(empty)'}
                      </div>
                    </div>}></DataField>
                  }
                </div>
              </>
            }

            {hasSectionChanges(orgTypeFields) &&
              <>
                <div style={{ marginBottom: '8px' }}>
                  <SubTitle>Type Information</SubTitle>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  {isChanged('org.orgTypeCd') &&
                    <DataField name='Type' value={BuildChange('org.orgTypeCd', types.map((item) => ({ key: parseInt(item.key), label: item.label })))}></DataField>
                  }

                  {isChanged('org.orgSubTypeCd') &&
                    <DataField name='Sub Type' value={BuildChange('org.orgSubTypeCd', subTypes.map((item) => ({ key: parseInt(item.key), label: item.label })))}></DataField>
                  }

                  {isChanged('org.campus') &&
                    <DataField name='Campus' value={BuildChange('org.campus')}></DataField>
                  }
                  {isChanged('org.charterInd') &&
                    <DataField name='Charter' value={BuildChange('org.charterInd')}></DataField>
                  }
                </div>
              </>
            }

            {(showProtected && hasSectionChanges(orgProprietaryFields)) &&
              <div>
                <div style={{ marginBottom: '8px' }}>
                  <SubTitle>Proprietary IDs</SubTitle>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  {isChanged('org.mdrPid') &&
                    <DataField name='MDR PID' value={BuildChange('org.mdrPid')}></DataField>
                  }

                  {isChanged('org.ipedsId') &&
                    <DataField name='IPEDS ID' value={BuildChange('org.ipedsId')}></DataField>
                  }
                  {isChanged('org.ncesId') &&
                    <DataField name='NCES ID' value={BuildChange('org.ncesId')}></DataField>
                  }
                  {isChanged('org.feeWaiverId') &&
                    <DataField name='Fee Waiver ID' value={BuildChange('org.feeWaiverId')}></DataField>
                  }
                </div>
              </div>
            }
          </> : <FieldNote>No Changes</FieldNote>
        }
      </Paper>

      <h6 style={{ marginBottom: '15px' }}>Code Information</h6>
      <Paper elevation={2} style={{ padding: '20px', marginBottom: '15px' }}>
        {hasSectionChanges(aiGeneralFields.concat(aiFlagFields)) ?
          <>
            {hasSectionChanges(aiGeneralFields) &&
              <>
                <div style={{ marginBottom: '15px' }}>
                  {isChanged('ai.aiCode') &&
                    <DataField name='Code' value={BuildChange('ai.aiCode')}></DataField>
                  }
                  {isChanged('ai.aiCategoryCd') &&
                    <DataField name='Type' value={BuildChange('ai.aiCategoryCd', aiCategoryOptions)}></DataField>
                  }
                  {isChanged('ai.aiLevelCd') &&
                    <DataField name='Level' value={BuildChange('ai.aiLevelCd', aiLevelOptions)}></DataField>
                  }
                  {isChanged('ai.initialIngestionPathCd') &&
                    <DataField name='Ingestion Path' value={BuildChange('ai.initialIngestionPathCd', ingestionOptions)}></DataField>
                  }
                  {isChanged('ai.aiStatusCd') &&
                    <DataField name='Status' value={BuildChange('ai.aiStatusCd', aiStatusOptions)}></DataField>
                  }
                </div>
              </>
            }
            {hasSectionChanges(aiFlagFields) &&
              <>
                <div style={{ marginBottom: '8px' }}>
                  <SubTitle>Program Flags</SubTitle>
                </div>

                <div>
                  {isChanged('ai.apEligibilityInd') &&
                    <DataField name='AP Eligibility' value={BuildChange('ai.apEligibilityInd')}></DataField>
                  }

                  {isChanged('ai.psatEligibilityInd') &&
                    <DataField name='PSAT Eligibility' value={BuildChange('ai.psatEligibilityInd')}></DataField>
                  }

                  {isChanged('ai.satEligibilityInd') &&
                    <DataField name='SAT Eligibility' value={BuildChange('ai.satEligibilityInd')}></DataField>
                  }

                  {isChanged('ai.clepEligibilityInd') &&
                    <DataField name='CLEP Eligibility' value={BuildChange('ai.clepEligibilityInd')}></DataField>
                  }
                  {isChanged('ai.ssdEligibilityInd') &&
                    <DataField name='SSD Eligibility' value={BuildChange('ai.ssdEligibilityInd')}></DataField>
                  }

                  {isChanged('ai.annualReviewInd') &&
                    <DataField name='Annual Reviews' value={BuildChange('ai.annualReviewInd')}></DataField>
                  }

                  {isChanged('ai.stateHomeSchoolInd') &&
                    <DataField name='State Home School Reporting' value={BuildChange('ai.stateHomeSchoolInd')}></DataField>
                  }

                  {isChanged('ai.crossPrgSecHoldInd') &&
                    <DataField name='Cross Program Security Hold' value={BuildChange('ai.crossPrgSecHoldInd')}></DataField>
                  }
                </div>
              </>
            }
          </> : <FieldNote>No Changes</FieldNote>
        }
      </Paper>
    </div>;
  }

  const { preparedBy, approvedBy, preparedDate, approvedDate } = data?.meta || {};

  return <div>
    <div style={{ marginBottom: '20px' }}>
      <div style={{ marginBottom: '5px' }}>
          <Highlight>Type: {summary.requestType.toUpperCase() === 'INTERNALUPDATE' ? 'UPDATE' : summary.requestType.toUpperCase()}</Highlight>
          <Highlight>Updated At: {formatDate(summary.updateDate)}</Highlight>
      </div>

      <div style={{ lineHeight: '18px' }}>
        <FieldNote>Prepared by {preparedBy} @ {formatDate(preparedDate || 0)}</FieldNote>
      </div>
      <div style={{ lineHeight: '18px' }}>
        <FieldNote>Approved by {approvedBy} @ {formatDate(approvedDate || 0)}</FieldNote>
      </div>
    </div>

    {details}
  </div>;
};

interface IAiProfileProps {
  record: IAIRecord;
  original?: IAIRecord;
  showDiff?: boolean;
  onlyChanges?: boolean;
}

export const AiProfile: FC<IAiProfileProps> = ({ record }) => {
  const { roles } = useAuth();
  const { types, subTypes, isLoading } = useReferences();
  const org = record.org || {};
  const ai = record.ai || {};

  const isAdmin = roles.includes('XPM_ADMIN');
  const showProtected = isAdmin;

    const categoryValue = aiCategoryOptions.find((item) => item.key === ai.aiCategoryCd)?.label || 'N/A';
    const ingestionValue = ingestionOptions.find((item) => item.key === ai.initialIngestionPathCd)?.label || 'N/A';
    const levelValue = aiLevelOptions.find((item) => item.key === ai.aiLevelCd)?.label || 'N/A';
    const statusName = aiStatusOptions.find((item) => item.key === ai.aiStatusCd)?.label || '';

    const statusValue = <div>
        {ai.aiStatusCd === 1 ? <RadioButtonCheckedIcon sx={{color: 'red', fontSize: 14}}/> :
            <RadioButtonCheckedIcon sx={{color: 'green', fontSize: 14}}/>} {statusName}
    </div>;

    const deliveryMethodMap = (deliveryMethod: any[]) => {
        return deliveryMethod.map(dm => {
            const dmm = dm;
            return DELIVERY_METHOD_OPTIONS[dmm];
        }).toString().replace(',', ', ');
    }

    const navigate = useNavigate();

    const editProfile = (record: IAIRecord) => {
        navigate(APP_URLS.PROFILE_EDIT_FORM, {
            state: {
                record: record,
                type: 'ai'
            }
        });
    };

    if (isLoading) {
        return <Loader></Loader>;
    } else {
        return <div style={{display: 'inline-block'}}>
            <Title>Organization Profile</Title>
            <Paper elevation={2} style={{padding: '20px', marginBottom: '15px'}}>
                <div style={{marginBottom: '15px'}}>
                    <DataField name='Name' value={org.name}></DataField>
                    <DataField name='Short Name' value={org.nameShort}></DataField>
                    <DataField name='Abbreviated Name' value={org.nameAbbr || 'N/A'}></DataField>
                    <DataField name='Website' value={org.website || 'N/A'}></DataField>
                    <DataField name='Phone' value={org.phone?.formatted || 'N/A'}></DataField>
                    <DataField name='Fax' value={org.fax?.formatted || 'N/A'}></DataField>
                    <DataField name='Email' value={org.email || 'N/A'}></DataField>
                </div>

                <div style={{marginBottom: '20px'}}>
          <DataField name={<>Visiting Address <PlaceIcon sx={{ fontSize: 16, color: '#bb2020' }}></PlaceIcon></>} value={formatAddress(org.visitingAddress)}></DataField>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <DataField name={<>Shipping Address <PlaceIcon sx={{ fontSize: 16, color: '#bb2020' }}></PlaceIcon></>} value={org.shippingAddress ? formatAddress(org.shippingAddress) : <i>No Shipping Address</i>}></DataField>
        </div>

        <div style={{ marginBottom: '8px' }}>
          <SubTitle>Type Information</SubTitle>
        </div>
        <div style={{ marginBottom: '15px' }}>
          <DataField name='Type' value={(types.find((item) => parseInt(item.key) === org.orgTypeCd)?.label) || ''}></DataField>
          <DataField name='Sub-Type' value={(subTypes.find((item) => parseInt(item.key) === org.orgSubTypeCd)?.label) || ''}></DataField>
          <DataField name='Campus' value={<Stack direction="row" spacing={1} alignItems="center">
            <div>No</div> <AntSwitch checked={org.campus || false}></AntSwitch><div>Yes</div>
          </Stack>}></DataField>

            <DataField name='Charter' value={<Stack direction="row" spacing={1} alignItems="center">
                <div>No</div>
                <AntSwitch checked={org.charterInd || false}></AntSwitch>
                <div>Yes</div>
            </Stack>}></DataField>
            <DataField name='Delivery Method' value={deliveryMethodMap(org?.educationDeliveryMethodCds || [])}/>
        </div>

        {showProtected &&
          <div>
            <div style={{ marginBottom: '8px' }}>
              <SubTitle>Proprietary IDs</SubTitle>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <DataField name='MDR PID' value={org.mdrPid || 'N/A'}></DataField>
              <DataField name='IPEDS ID' value={org.ipedsId || 'N/A'}></DataField>
              <DataField name='NCES ID' value={org.ncesId || 'N/A'}></DataField>
              <DataField name='Fee Waiver ID' value={org.feeWaiverId || 'N/A'}></DataField>
            </div>
          </div>
        }
      </Paper>

      <Title>Code Information</Title>
      <Paper elevation={2} style={{ padding: '20px', marginBottom: '15px' }}>
        <div style={{ marginBottom: '15px' }}>
          <DataField name='Code' value={ai.aiCode}></DataField>
          <DataField name='Type' value={categoryValue}></DataField>
          <DataField name='Level' value={levelValue}></DataField>
          <DataField name='Ingestion Path' value={ingestionValue}></DataField>
          <DataField name='Status' value={statusValue}></DataField>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <SubTitle>Program Flags</SubTitle>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '75px' }}>
          <div>
            <DataField name='AP Eligibility' value={<Stack direction="row" spacing={1} alignItems="center">
              <div>No</div> <AntSwitch checked={ai.apEligibilityInd || false}></AntSwitch><div>Yes</div>
            </Stack>}></DataField>

            <DataField name='PSAT Eligibility' value={<Stack direction="row" spacing={1} alignItems="center">
              <div>No</div> <AntSwitch checked={ai.psatEligibilityInd || false}></AntSwitch><div>Yes</div>
            </Stack>}></DataField>

            <DataField name='SAT Eligibility' value={<Stack direction="row" spacing={1} alignItems="center">
              <div>No</div> <AntSwitch checked={ai.satEligibilityInd || false}></AntSwitch><div>Yes</div>
            </Stack>}></DataField>

            <DataField name='CLEP Eligibility' value={<Stack direction="row" spacing={1} alignItems="center">
              <div>No</div> <AntSwitch checked={ai.clepEligibilityInd || false}></AntSwitch><div>Yes</div>
            </Stack>}></DataField>
          </div>
          <div>
            <DataField name='SSD Eligibility' value={<Stack direction="row" spacing={1} alignItems="center">
              <div>No</div> <AntSwitch checked={ai.ssdEligibilityInd || false}></AntSwitch><div>Yes</div>
            </Stack>}></DataField>

            <DataField name='Annual Reviews' value={<Stack direction="row" spacing={1} alignItems="center">
              <div>No</div> <AntSwitch checked={ai.annualReviewInd || false}></AntSwitch><div>Yes</div>
            </Stack>}></DataField>

            <DataField name='State Home School Reporting' value={<Stack direction="row" spacing={1} alignItems="center">
                <div>No</div>
                <AntSwitch checked={ai.stateHomeSchoolInd || false}></AntSwitch>
                <div>Yes</div>
            </Stack>}></DataField>

              <DataField name='Cross Program Security Hold'
                         value={<Stack direction="row" spacing={1} alignItems="center">
                             <div>No</div>
                             <AntSwitch checked={ai.crossPrgSecHoldInd || false}></AntSwitch>
                             <div>Yes</div>
                         </Stack>}></DataField>

          </div>
        </div>
      </Paper>
            {showProtected &&
                <div>
                    <PrimaryButton style={{width: '40%', marginBottom: '10px', marginLeft: '200px'}}
                                   onClick={() => {
                                       editProfile(record)
                                   }}>Edit
                    </PrimaryButton>
                </div>
            }
        </div>;
  }
};


export const ProfilePage: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as any;
  const aiCode = state?.aiCode;
  const orgId = state?.orgId;
  const searchParams = state?.search;

  const { data, isLoading } = useAi(orgId, aiCode);
  const { data: history, isLoading: historyLoading } = useHistory(orgId, aiCode);

  if (isLoading) {
    return <Loader />;
  } else {
    return <div>
      <Grid container>
        <Grid item xs={7} style={{ paddingRight: '15px' }}>
          {data &&
            <AiProfile record={data} />
          }
        </Grid>
        <Grid item xs={5} style={{
          borderLeft: '2px solid #EEEEEE',
          paddingLeft: '15px',
          paddingRight: '15px',
          height: '100vh',
          overflowY: 'scroll'
        }}>
          <Title>Change History</Title>
          {historyLoading ?
            <Loader></Loader> :
            history.length > 0 ? history.map((item) => {
              return <>
                <HistoryDetails summary={item} />
                <Divider style={{ margin: '20px 0px' }} />
              </>;
            }) : <Alert severity='info'>No History Found</Alert>
          }
        </Grid>
        <Button style={{ width: '100%' }} onClick={() => navigate(APP_URLS.SEARCH, { state: { search: searchParams, type: 'ai' } })}>Close</Button>
      </Grid>
    </div>;
  }
};
